import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Stack,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { 
  getScrutinyRemarks,
  createScrutinyRemark,
  updateScrutinyRemark,
  deleteScrutinyRemark,
  getSectionNames
} from '../../services/api';

const ScrutinyRemarksManagement = () => {
  const [loading, setLoading] = useState(false);
  const [remarks, setRemarks] = useState([]);
  const [filteredRemarks, setFilteredRemarks] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Form state
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRemark, setEditingRemark] = useState(null);
  const [formData, setFormData] = useState({
    sectionName: '',
    type: '',
    inadequateRemarks: ''
  });

  // Filters
  const [sectionFilter, setSectionFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sectionOptions, setSectionOptions] = useState([]);

  // Messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRemark, setSelectedRemark] = useState(null);



  // View details dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewRemark, setViewRemark] = useState(null);

  const typeOptions = ['Assessment', 'Document Review'];
  
  // Predefined NABL Medical Application Section Names
  const predefinedSectionNames = [
    'Adverse Action History',
    'Category of Medical Testing Facility',
    'Collection of Sample From',
    'Contact Person from laboratory for NABL',
    'Declaration Section',
    'Equipment',
    'Internal Audit and Management Review',
    'Laboratory Details',
    'Legal Entity Type',
    'NABL Accreditation Consultancy',
    'Non-Analytical Instruments',
    'Organization Details',
    'Parent Organization',
    'Proficiency Testing',
    'Proficiency Testing Document',
    'Reference Material',
    'Scope',
    'SEZ Region',
    'Staff',
    'Type of Functioning'
  ];

  useEffect(() => {
    fetchRemarks();
    // Initialize section options with predefined names and fetch existing ones
    initializeSectionNames();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [remarks, sectionFilter, typeFilter]);

  const fetchRemarks = async () => {
    try {
      setLoading(true);
      const response = await getScrutinyRemarks();
      if (response.data.success) {
        setRemarks(response.data.data || []);
        setTotalCount(response.data.data?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching remarks:', error);
      setErrorMessage('Failed to fetch scrutiny remarks');
    } finally {
      setLoading(false);
    }
  };

  const initializeSectionNames = async () => {
    try {
      // Start with predefined section names
      let allSectionNames = [...predefinedSectionNames];
      
      // Fetch existing section names from database
      const response = await getSectionNames();
      if (response.data.success && response.data.data) {
        const existingNames = response.data.data;
        
        // Add any existing names that are not in predefined list
        existingNames.forEach(name => {
          if (!allSectionNames.includes(name)) {
            allSectionNames.push(name);
          }
        });
      }
      
      // Sort alphabetically for better UX
      allSectionNames.sort();
      setSectionOptions(allSectionNames);
    } catch (error) {
      console.error('Error fetching section names:', error);
      // Fallback to predefined names only if API fails
      setSectionOptions([...predefinedSectionNames].sort());
    }
  };

  const fetchSectionNames = async () => {
    // This function is kept for backward compatibility and refreshing
    await initializeSectionNames();
  };

  const applyFilters = () => {
    let filtered = remarks;

    if (sectionFilter) {
      filtered = filtered.filter(remark =>
        remark.section_name?.toLowerCase().includes(sectionFilter.toLowerCase())
      );
    }

    if (typeFilter) {
      filtered = filtered.filter(remark => remark.type === typeFilter);
    }

    setFilteredRemarks(filtered);
    setPage(0);
  };

  const clearFilters = () => {
    setSectionFilter('');
    setTypeFilter('');
  };

  const handleOpenDialog = (remark = null) => {
    if (remark) {
      setEditingRemark(remark);
      setFormData({
        sectionName: remark.section_name || '',
        type: remark.type || '',
        inadequateRemarks: remark.inadequate_remarks || ''
      });
    } else {
      setEditingRemark(null);
      setFormData({
        sectionName: '',
        type: '',
        inadequateRemarks: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRemark(null);
    setFormData({
      sectionName: '',
      type: '',
      inadequateRemarks: ''
    });
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.sectionName.trim() || !formData.type || !formData.inadequateRemarks.trim()) {
        setErrorMessage('All fields are required');
        return;
      }

      setLoading(true);

      const data = {
        sectionName: formData.sectionName.trim(),
        type: formData.type,
        inadequateRemarks: formData.inadequateRemarks.trim(),
        createdBy: localStorage.getItem('orgId') || '',
        updatedBy: localStorage.getItem('orgId') || ''
      };

      let response;
      if (editingRemark) {
        response = await updateScrutinyRemark(editingRemark.id, data);
      } else {
        response = await createScrutinyRemark(data);
      }

      if (response.data.success) {
        setSuccessMessage(editingRemark ? 'Scrutiny remark updated successfully' : 'Scrutiny remark created successfully');
        handleCloseDialog();
        fetchRemarks();
        fetchSectionNames();
      } else {
        setErrorMessage(response.data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving remark:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to save scrutiny remark');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (remark) => {
    if (!window.confirm(`Are you sure you want to delete the remark for "${remark.section_name}"?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await deleteScrutinyRemark(remark.id);
      if (response.data.success) {
        setSuccessMessage('Scrutiny remark deleted successfully');
        fetchRemarks();
        fetchSectionNames();
      } else {
        setErrorMessage(response.data.message || 'Failed to delete scrutiny remark');
      }
    } catch (error) {
      console.error('Error deleting remark:', error);
      setErrorMessage('Failed to delete scrutiny remark');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, remark) => {
    setAnchorEl(event.currentTarget);
    setSelectedRemark(remark);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRemark(null);
  };

  const handleView = (remark) => {
    setViewRemark(remark);
    setViewDialogOpen(true);
    handleMenuClose();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#00024c' }}>
        Scrutiny Remarks Management
      </Typography>

      {/* Statistics Card */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {totalCount}
              </Typography>
              <Typography variant="body2">Total Scrutiny Remarks</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {remarks.filter(r => r.type === 'Assessment').length}
              </Typography>
              <Typography variant="body2">Assessment Remarks</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            background: 'linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {remarks.filter(r => r.type === 'Document Review').length}
              </Typography>
              <Typography variant="body2">Document Review Remarks</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Actions and Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              options={sectionOptions}
              value={sectionFilter}
              onChange={(event, newValue) => setSectionFilter(newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Section"
                  placeholder="Select or type section name"
                />
              )}
              freeSolo
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Filter by Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {typeOptions.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                size="small"
                variant="outlined"
              >
                Clear Filters
              </Button>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchRemarks}
                size="small"
                variant="outlined"
              >
                Refresh
              </Button>
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                variant="contained"
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                  }
                }}
              >
                Add Remark
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>S. No</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Section Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>Inadequate Remarks</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography>Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredRemarks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No scrutiny remarks found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRemarks
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((remark, index) => (
                    <TableRow key={remark.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {remark.section_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={remark.type}
                          color={remark.type === 'Assessment' ? 'primary' : 'secondary'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            maxWidth: 300
                          }}
                        >
                          {remark.inadequate_remarks}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(remark.created_date)}
                        </Typography>
                        {remark.created_by && (
                          <Typography variant="caption" color="text.secondary">
                            by {remark.created_by}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, remark)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredRemarks.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleView(selectedRemark)}>
          <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => { handleOpenDialog(selectedRemark); handleMenuClose(); }}>
          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => { handleDelete(selectedRemark); handleMenuClose(); }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}>
          {editingRemark ? 'Edit Scrutiny Remark' : 'Add New Scrutiny Remark'}
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={sectionOptions}
                value={formData.sectionName}
                onChange={(event, newValue) => handleFormChange('sectionName', newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Section Name"
                    placeholder="Select or enter section name"
                    required
                  />
                )}
                freeSolo
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => handleFormChange('type', e.target.value)}
                  label="Type *"
                >
                  {typeOptions.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={6}
                label="Inadequate Remarks *"
                value={formData.inadequateRemarks}
                onChange={(e) => handleFormChange('inadequateRemarks', e.target.value)}
                placeholder="Enter detailed inadequate remarks..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
              }
            }}
          >
            {loading ? 'Saving...' : editingRemark ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}>
          Scrutiny Remark Details
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          {viewRemark && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Section Name</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{viewRemark.section_name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                <Chip
                  label={viewRemark.type}
                  color={viewRemark.type === 'Assessment' ? 'primary' : 'secondary'}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary">Inadequate Remarks</Typography>
                <Paper sx={{ p: 2, mt: 1, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewRemark.inadequate_remarks}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Created Date</Typography>
                <Typography variant="body2">{formatDate(viewRemark.created_date)}</Typography>
                {viewRemark.created_by && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Created By</Typography>
                    <Typography variant="body2">{viewRemark.created_by}</Typography>
                  </>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {viewRemark.updated_date && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary">Updated Date</Typography>
                    <Typography variant="body2">{formatDate(viewRemark.updated_date)}</Typography>
                    {viewRemark.updated_by && (
                      <>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Updated By</Typography>
                        <Typography variant="body2">{viewRemark.updated_by}</Typography>
                      </>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>


      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScrutinyRemarksManagement;